import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import apiService from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import './CoursePlayerPage.css'; 

const CoursePlayerPage = () => {
  const { t, language } = useLanguage();
  const { id: courseId } = useParams();
  const navigate = useNavigate();

  const [courseData, setCourseData] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEnrolledCourse = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.getEnrolledCourse(courseId);
        if (response.success) {
          const course = response.data.course;
          const enrollmentData = response.data.enrollment;
          setCourseData(course);
          setEnrollment(enrollmentData);

          // Find the first accessible lesson to start with
          let firstAccessibleLesson = null;
          for (const module of course.modules.sort((a, b) => a.order - b.order)) {
            if (module.hasAccess && module.lessons?.length > 0) {
              firstAccessibleLesson = module.lessons.sort((a, b) => a.order - b.order)[0];
              break;
            }
          }
          setCurrentLesson(firstAccessibleLesson);

        } else {
          throw new Error(response.message || t('coursePlayer.errors.fetchCourseContent'));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEnrolledCourse();
  }, [courseId]);

  const handleMarkAsComplete = async () => {
    if (!currentLesson || isLessonCompleted(currentLesson._id)) return;
    try {
      const response = await apiService.markLessonAsComplete(currentLesson._id);
      if (response.success) {
        setEnrollment(response.data); // Update enrollment with new progress
      }
    } catch (err) {
      console.error("Failed to mark lesson as complete:", err);
    }
  };

  const isLessonCompleted = (lessonId) => {
    return enrollment?.progress.some(p => p.lesson === lessonId);
  };

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const toTranslationKey = (value = '') => value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const getDynamicTitleFallback = (text = '', contentType = 'generic') => {
    const normalized = text.trim().toLowerCase().replace(/\s+/g, ' ');
    const advancedTitleMatch = text.trim().match(/^yoga\s*[–-]\s*fundamental(s)?\s*\((advance|advanced)\)$/i);

    if (language === 'hi') {
      if (contentType === 'courseTitle' && /^yoga\s*[–-]\s*fundamental(s)?$/i.test(text.trim())) {
        return 'योग – मूलभूत';
      }
      if (contentType === 'courseTitle' && advancedTitleMatch) {
        return 'योग – मूलभूत (उन्नत)';
      }
      const moduleMatch = text.trim().match(/^yoga\s+fundamental\s+([ivx]+)\s*[-–]\s*basics$/i);
      if (contentType === 'moduleTitle' && moduleMatch) {
        return `योग मूलभूत ${moduleMatch[1].toUpperCase()} - आधार`;
      }
      const advancedModuleMatch = text.trim().match(/^yoga\s*[-–]\s*fundamentals?\s*\((advanced?|advance)\s*([ivx]+)\)$/i);
      if (contentType === 'moduleTitle' && advancedModuleMatch) {
        return `योग - मूलभूत (उन्नत ${advancedModuleMatch[2].toUpperCase()})`;
      }
      const lessonMatch = text.trim().match(/^day\s*(\d+)\s*[-–]\s*yoga\s+basics$/i);
      if (contentType === 'lessonTitle' && lessonMatch) {
        return `दिन ${lessonMatch[1]} - योग आधारभूत अभ्यास`;
      }
      const advancedLessonMatch = text.trim().match(/^yoga\s+advanc(?:e|ed)\s*[-–]\s*module\s*([ivx]+)$/i);
      if (contentType === 'lessonTitle' && advancedLessonMatch) {
        return `योग उन्नत - मॉड्यूल ${advancedLessonMatch[1].toUpperCase()}`;
      }
    }

    if (language === 'or') {
      if (contentType === 'courseTitle' && /^yoga\s*[–-]\s*fundamental(s)?$/i.test(text.trim())) {
        return 'ଯୋଗ – ମୂଳଭିତ୍ତି';
      }
      if (contentType === 'courseTitle' && advancedTitleMatch) {
        return 'ଯୋଗ – ମୂଳଭିତ୍ତି (ଉନ୍ନତ)';
      }
      const moduleMatch = text.trim().match(/^yoga\s+fundamental\s+([ivx]+)\s*[-–]\s*basics$/i);
      if (contentType === 'moduleTitle' && moduleMatch) {
        return `ଯୋଗ ମୂଳଭିତ୍ତି ${moduleMatch[1].toUpperCase()} - ଆଧାର`;
      }
      const advancedModuleMatch = text.trim().match(/^yoga\s*[-–]\s*fundamentals?\s*\((advanced?|advance)\s*([ivx]+)\)$/i);
      if (contentType === 'moduleTitle' && advancedModuleMatch) {
        return `ଯୋଗ - ମୂଳଭିତ୍ତି (ଉନ୍ନତ ${advancedModuleMatch[2].toUpperCase()})`;
      }
      const lessonMatch = text.trim().match(/^day\s*(\d+)\s*[-–]\s*yoga\s+basics$/i);
      if (contentType === 'lessonTitle' && lessonMatch) {
        return `ଦିନ ${lessonMatch[1]} - ଯୋଗ ମୂଳ ଅଭ୍ୟାସ`;
      }
      const advancedLessonMatch = text.trim().match(/^yoga\s+advanc(?:e|ed)\s*[-–]\s*module\s*([ivx]+)$/i);
      if (contentType === 'lessonTitle' && advancedLessonMatch) {
        return `ଯୋଗ ଉନ୍ନତ - ମଡ୍ୟୁଲ ${advancedLessonMatch[1].toUpperCase()}`;
      }
    }

    if (contentType === 'moduleTitle' && /^yoga\s+fundamental\s+([ivx]+)\s*[-–]\s*basics$/i.test(normalized)) {
      return text.replace(/yoga fundamental/i, 'Yoga Fundamental');
    }
    return text;
  };

  const translateDynamicText = (path, fallbackText, contentType) => {
    const translated = t(path, {}, fallbackText);
    if (translated !== fallbackText) return translated;
    return getDynamicTitleFallback(fallbackText, contentType);
  };

  const getTranslatedCourseTitle = (title) => (
    translateDynamicText(`bhaktiYogaCourseDetail.courses.${toTranslationKey(title)}.title`, title, 'courseTitle')
  );

  const getTranslatedModuleTitle = (title) => (
    translateDynamicText(`bhaktiYogaCourseDetail.modules.${toTranslationKey(title)}.title`, title, 'moduleTitle')
  );

  const getTranslatedLessonTitle = (title) => (
    translateDynamicText(`bhaktiYogaCourseDetail.lessons.${toTranslationKey(title)}.title`, title, 'lessonTitle')
  );

  const getTranslatedTier = (tier) => (
    t(`bhaktiYogaPage.tiers.${toTranslationKey(tier)}`, {}, tier)
  );

  const numberLocale = language === 'hi' ? 'hi-IN' : language === 'or' ? 'or-IN' : 'en-IN';

  const totalLessons = courseData?.modules.reduce((acc, module) => acc + (module.lessons?.length || 0), 0) || 0;
  const completedLessons = enrollment?.progress.length || 0;
  const completionPercentage = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;

  // Find the module that the current lesson belongs to
  const currentModule = currentLesson
    ? courseData?.modules.find(m => m.lessons.some(l => l._id === currentLesson._id))
    : null;

  const videoId = getYouTubeId(currentLesson?.videoUrl);

  if (isLoading) { return <div className="loading-spinner">{t('coursePlayer.loading')}</div>; }
  if (error) { return <div className="error-message">{t('coursePlayer.errorPrefix')}: {error}</div>; }
  if (!courseData) { return <div className="error-message">{t('coursePlayer.courseContentNotFound')}</div>; }

  return (
    <div className="course-player-page">
      <aside className="player-sidebar">
        <div className="sidebar-header">
          <Link to="/dashboard/courses" className="back-link">
            <i className="fas fa-arrow-left"></i> {t('dashboard.sidebar.myCourses')}
          </Link>
          <h3>{getTranslatedCourseTitle(courseData.title)}</h3>
        </div>
        <div className="course-progress">
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${completionPercentage}%` }}></div>
          </div>
          <p className="progress-text">{t('coursePlayer.progressComplete', {
            percent: Math.round(completionPercentage).toLocaleString(numberLocale),
            completed: completedLessons.toLocaleString(numberLocale),
            total: totalLessons.toLocaleString(numberLocale),
          })}</p>
        </div>
        <div className="curriculum-list">
          {courseData.modules.sort((a, b) => a.order - b.order).map((module) => (
            <div key={module._id} className="module-group">
              <h4 className="module-title">
                {getTranslatedModuleTitle(module.title)}
                {!module.hasAccess && <i className="fas fa-lock lock-icon"></i>}
              </h4>
              <ul className="lesson-list">
                {module.lessons.sort((a, b) => a.order - b.order).map((lesson) => (
                  <li
                    key={lesson._id}
                    className={`lesson-item ${!module.hasAccess ? 'locked' : ''} ${currentLesson?._id === lesson._id ? 'active' : ''}`}
                    onClick={() => module.hasAccess && setCurrentLesson(lesson)}
                  >
                    <i className={`fas ${!module.hasAccess ? 'fa-lock' : (isLessonCompleted(lesson._id) ? 'fa-check-circle completed-icon' : (lesson.type === 'Video' ? 'fa-play-circle' : 'fa-file-alt'))}`}></i>
                    <span>{getTranslatedLessonTitle(lesson.title)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </aside>
      <main className="player-main-content">
        {currentLesson ? (
          <>
            <div className="video-player-wrapper">
              {/* --- CRUCIAL ACCESS CHECK --- */}
              {currentModule?.hasAccess ? (
                // User has access, show the content
                currentLesson.type === 'Video' && videoId ? (
                  <LiteYouTubeEmbed id={videoId} title={getTranslatedLessonTitle(currentLesson.title)} />
                ) : (
                  <div className="non-video-content">
                    <h3><i className={`fas ${currentLesson.type === 'PDF' ? 'fa-file-pdf' : 'fa-file-alt'}`}></i> {getTranslatedLessonTitle(currentLesson.title)}</h3>
                    <p>{currentLesson.textContent || t('coursePlayer.nonVideoFallback')}</p>
                  </div>
                )
              ) : (
                // User does NOT have access, show the upsell message
                <div className="content-locked-overlay">
                  <i className="fas fa-lock"></i>
                  <h3>{t('coursePlayer.lockedContentTitle')}</h3>
                  <p>{t('coursePlayer.lockedContentMessage', { tier: getTranslatedTier(currentModule?.subscriptionTier) })}</p>
                  <button onClick={() => navigate('/subscribe')} className="btn-subscribe-now">
                    {t('coursePlayer.upgradePlan')}
                  </button>
                </div>
              )}
            </div>

            <div className="lesson-header">
              <h1>{getTranslatedLessonTitle(currentLesson.title)}</h1>
              {currentModule?.hasAccess && (
                <button
                  className={`btn-complete ${isLessonCompleted(currentLesson._id) ? 'completed' : ''}`}
                  onClick={handleMarkAsComplete}
                  disabled={isLessonCompleted(currentLesson._id)}
                >
                  {isLessonCompleted(currentLesson._id) ? t('coursePlayer.completed') : t('coursePlayer.markAsComplete')}
                </button>
              )}
            </div>

            {currentModule?.hasAccess && currentLesson.notesUrl && (
              <div className="lesson-details">
                <a href={currentLesson.notesUrl} target="_blank" rel="noopener noreferrer" className="btn-download-notes">
                  <i className="fas fa-download"></i> {t('coursePlayer.viewOrDownloadNotes')}
                </a>
              </div>
            )}
          </>
        ) : (
          <div className="no-lesson-selected">
            <h3>{t('coursePlayer.welcomeTitle')}</h3>
            <p>{t('coursePlayer.selectLessonPrompt')}</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default CoursePlayerPage;
